/**
 * DuoCast Signaling Protocol v1 — TypeScript Type Definitions
 *
 * BE-014: Complete type system for the WebSocket signaling protocol.
 * All messages are JSON-encoded and transmitted over WSS.
 *
 * Protocol version: 1
 * Transport: wss://signal.duocast.ai?v=1
 */

// ---------------------------------------------------------------------------
// Primitive Types & Enums
// ---------------------------------------------------------------------------

/** Protocol version. Always 1. */
export const PROTOCOL_VERSION = 1 as const;

/** All possible message type discriminators. */
export type MessageType =
  | "JOIN"
  | "MATCHED"
  | "OFFER"
  | "ANSWER"
  | "ICE"
  | "NEXT"
  | "PEER_LEFT"
  | "STAGE_CHANGE"
  | "REVEAL_REQUEST"
  | "REVEAL_ACCEPT"
  | "REVEAL_WAITING"
  | "REPORT"
  | "ERROR"
  | "RATE_LIMITED"
  | "BANNED"
  | "HEARTBEAT"
  | "PONG"
  | "AI_PARTNER";

/** Message types sent from client to server. */
export type ClientMessageType =
  | "JOIN"
  | "OFFER"
  | "ANSWER"
  | "ICE"
  | "NEXT"
  | "REPORT"
  | "REVEAL_REQUEST"
  | "HEARTBEAT";

/** Message types sent from server to client. */
export type ServerMessageType =
  | "MATCHED"
  | "OFFER"
  | "ANSWER"
  | "ICE"
  | "PEER_LEFT"
  | "STAGE_CHANGE"
  | "REVEAL_ACCEPT"
  | "REVEAL_WAITING"
  | "ERROR"
  | "RATE_LIMITED"
  | "BANNED"
  | "PONG"
  | "AI_PARTNER";

/** Session stages in the progressive reveal flow. */
export type SessionStage = 1 | 2 | 3;

/** Role assigned to each peer in a matched room. */
export type PeerRole = "initiator" | "responder";

/** Reason the other peer left the room. */
export type PeerLeftReason = "skipped" | "disconnected" | "banned";

/** Categories for reporting a peer. */
export type ReportCategory = "inappropriate" | "harassment" | "spam" | "underage";

/** Gender attribute values. */
export type Gender = "male" | "female" | "other";

/** Gender preference for matching. */
export type GenderPreference = "male" | "female" | "any";

/**
 * Error codes organized by domain.
 *
 * E1000-E1099: Protocol errors (malformed messages, invalid versions)
 * E2000-E2099: Authentication & rate limiting errors
 * E3000-E3099: Session errors (room state, role violations)
 */
export enum ErrorCode {
  // -- Protocol Errors (E1000-E1099) --
  INVALID_VERSION = "E1000",
  DUPLICATE_JOIN = "E1001",
  INVALID_MESSAGE = "E1002",
  UNKNOWN_TYPE = "E1003",
  MESSAGE_TOO_LARGE = "E1004",
  INVALID_SDP = "E1005",
  INVALID_CANDIDATE = "E1006",

  // -- Auth & Rate Limiting Errors (E2000-E2099) --
  AUTH_REQUIRED = "E2000",
  AUTH_EXPIRED = "E2001",
  AUTH_INVALID = "E2002",
  RATE_LIMITED = "E2003",
  IP_BLOCKED = "E2004",
  BANNED = "E2005",

  // -- Session Errors (E3000-E3099) --
  ROOM_NOT_FOUND = "E3000",
  NOT_IN_ROOM = "E3001",
  DUPLICATE_REVEAL = "E3002",
  DUPLICATE_REPORT = "E3003",
  INVALID_ROLE = "E3004",
  ROOM_CLOSED = "E3005",
  STAGE_NOT_REACHED = "E3006",
  NOT_IN_QUEUE = "E3007",
}

// ---------------------------------------------------------------------------
// Shared Sub-types
// ---------------------------------------------------------------------------

/** Client-inferred attributes sent with JOIN. */
export interface JoinAttributes {
  /** Estimated age (13-99). */
  age?: number;
  /** Estimated gender. */
  gender?: Gender;
  /** ISO 3166-1 alpha-2 country/region code. */
  geoRegion?: string;
  /** BCP 47 language tag. */
  language?: string;
}

/** User-set matching preferences sent with JOIN. */
export interface JoinPreferences {
  /** Desired peer age range [min, max]. Min >= 13, Max <= 99. */
  ageRange?: [number, number];
  /** Preferred peer gender. Defaults to "any". */
  genderPref?: GenderPreference;
}

/** ICE candidate object relayed between peers. */
export interface IceCandidate {
  /** The ICE candidate string. */
  candidate: string;
  /** Media stream identification tag. */
  sdpMid?: string | null;
  /** Index of the media description in the SDP. */
  sdpMLineIndex?: number | null;
}

// ---------------------------------------------------------------------------
// Base Message
// ---------------------------------------------------------------------------

/** Fields common to every signaling message. */
export interface BaseMessage {
  /** Protocol version. Always 1. */
  v: typeof PROTOCOL_VERSION;
  /** Message type discriminator. */
  type: MessageType;
}

// ---------------------------------------------------------------------------
// Client -> Server Messages
// ---------------------------------------------------------------------------

/** JOIN: Enter the matching queue with attributes and preferences. */
export interface JoinMessage extends BaseMessage {
  type: "JOIN";
  /** Client-inferred attributes for matching. */
  attributes: JoinAttributes;
  /** Optional user-set preferences. */
  preferences?: JoinPreferences;
  /** Optional room ID for direct join (skip matching queue). */
  roomId?: string;
}

/** OFFER: WebRTC SDP offer (sent by initiator, relayed to responder). */
export interface OfferMessage extends BaseMessage {
  type: "OFFER";
  /** Room this offer belongs to. */
  roomId: string;
  /** SDP offer string. */
  sdp: string;
}

/** ANSWER: WebRTC SDP answer (sent by responder, relayed to initiator). */
export interface AnswerMessage extends BaseMessage {
  type: "ANSWER";
  /** Room this answer belongs to. */
  roomId: string;
  /** SDP answer string. */
  sdp: string;
}

/** ICE: Trickle ICE candidate (relayed between peers). */
export interface IceMessage extends BaseMessage {
  type: "ICE";
  /** Room this candidate belongs to. */
  roomId: string;
  /** ICE candidate object. */
  candidate: IceCandidate;
}

/** NEXT: Skip current peer and re-enter matching queue. */
export interface NextMessage extends BaseMessage {
  type: "NEXT";
  /** Room to leave. */
  roomId: string;
}

/** REPORT: Report the current peer for a violation. */
export interface ReportMessage extends BaseMessage {
  type: "REPORT";
  /** Room containing the peer to report. */
  roomId: string;
  /** Report category. */
  category: ReportCategory;
}

/** REVEAL_REQUEST: Request identity reveal (Stage 3 opt-in). */
export interface RevealRequestMessage extends BaseMessage {
  type: "REVEAL_REQUEST";
  /** Room to request reveal in. */
  roomId: string;
}

/** HEARTBEAT: Client keepalive ping. Sent every 30 seconds. */
export interface HeartbeatMessage extends BaseMessage {
  type: "HEARTBEAT";
}

// ---------------------------------------------------------------------------
// Server -> Client Messages
// ---------------------------------------------------------------------------

/** MATCHED: A match has been found; begin WebRTC negotiation. */
export interface MatchedMessage extends BaseMessage {
  type: "MATCHED";
  /** Unique room identifier (UUID v4). */
  roomId: string;
  /** Opaque identifier for the matched peer. */
  peerId: string;
  /** Role in the WebRTC negotiation. */
  role: PeerRole;
}

/** PEER_LEFT: The other peer left the room. */
export interface PeerLeftMessage extends BaseMessage {
  type: "PEER_LEFT";
  /** Room the peer left. */
  roomId: string;
  /** Reason the peer left. */
  reason: PeerLeftReason;
}

/** STAGE_CHANGE: Session advanced to the next stage. */
export interface StageChangeMessage extends BaseMessage {
  type: "STAGE_CHANGE";
  /** Room the stage change applies to. */
  roomId: string;
  /** New stage (2 = filtered video, 3 = clear video). */
  stage: 2 | 3;
  /** Unix timestamp in milliseconds when the stage changed. */
  timestamp: number;
}

/** REVEAL_ACCEPT: Both peers agreed to reveal (mutual opt-in). */
export interface RevealAcceptMessage extends BaseMessage {
  type: "REVEAL_ACCEPT";
  /** Room where reveal was accepted. */
  roomId: string;
}

/** REVEAL_WAITING: You revealed; waiting for peer to reveal. */
export interface RevealWaitingMessage extends BaseMessage {
  type: "REVEAL_WAITING";
  /** Room where client is waiting for peer to reveal. */
  roomId: string;
}

/** ERROR: An operation failed. */
export interface ErrorMessage extends BaseMessage {
  type: "ERROR";
  /** Structured error code (see ErrorCode enum). */
  code: string;
  /** Human-readable error description. */
  message: string;
  /** Optional structured error details. */
  details?: Record<string, unknown>;
}

/** RATE_LIMITED: Client exceeded rate limits. */
export interface RateLimitedMessage extends BaseMessage {
  type: "RATE_LIMITED";
  /** Milliseconds before the client should retry. */
  retryAfterMs: number;
}

/** BANNED: User has been banned. Connection will be closed. */
export interface BannedMessage extends BaseMessage {
  type: "BANNED";
  /** ISO 8601 datetime when the ban expires. */
  expiresAt: string;
  /** Human-readable ban reason. */
  reason: string;
}

/** PONG: Server response to HEARTBEAT. */
export interface PongMessage extends BaseMessage {
  type: "PONG";
  /** Server Unix timestamp in milliseconds. */
  serverTime: number;
  /** Approximate number of currently connected users. */
  onlineCount: number;
}

/** AI_PARTNER: Matched with an AI conversation partner (low-traffic fallback). */
export interface AiPartnerMessage extends BaseMessage {
  type: "AI_PARTNER";
  /** Room identifier for the AI session. */
  roomId: string;
}

// ---------------------------------------------------------------------------
// Union Types
// ---------------------------------------------------------------------------

/** All client-to-server message types. */
export type ClientMessage =
  | JoinMessage
  | OfferMessage
  | AnswerMessage
  | IceMessage
  | NextMessage
  | ReportMessage
  | RevealRequestMessage
  | HeartbeatMessage;

/** All server-to-client message types. */
export type ServerMessage =
  | MatchedMessage
  | OfferMessage
  | AnswerMessage
  | IceMessage
  | PeerLeftMessage
  | StageChangeMessage
  | RevealAcceptMessage
  | RevealWaitingMessage
  | ErrorMessage
  | RateLimitedMessage
  | BannedMessage
  | PongMessage
  | AiPartnerMessage;

/** Discriminated union of every signaling message. */
export type SignalingMessage = ClientMessage | ServerMessage;

// ---------------------------------------------------------------------------
// Type Guards
// ---------------------------------------------------------------------------

/** Set of message types that are exclusively client-to-server. */
const CLIENT_ONLY_TYPES: ReadonlySet<string> = new Set<ClientMessageType>([
  "JOIN",
  "NEXT",
  "REPORT",
  "REVEAL_REQUEST",
  "HEARTBEAT",
]);

/** Set of message types that are exclusively server-to-client. */
const SERVER_ONLY_TYPES: ReadonlySet<string> = new Set<ServerMessageType>([
  "MATCHED",
  "PEER_LEFT",
  "STAGE_CHANGE",
  "REVEAL_ACCEPT",
  "REVEAL_WAITING",
  "ERROR",
  "RATE_LIMITED",
  "BANNED",
  "PONG",
  "AI_PARTNER",
]);

/**
 * Type guard: returns true if the message is a client-to-server message.
 *
 * Note: OFFER, ANSWER, and ICE are bidirectional (client sends, server relays).
 * This guard returns true for those as well since the client is the original sender.
 */
export function isClientMessage(msg: SignalingMessage): msg is ClientMessage {
  return (
    CLIENT_ONLY_TYPES.has(msg.type) ||
    msg.type === "OFFER" ||
    msg.type === "ANSWER" ||
    msg.type === "ICE"
  );
}

/**
 * Type guard: returns true if the message is a server-to-client message.
 *
 * Note: OFFER, ANSWER, and ICE are bidirectional (server relays to client).
 * This guard returns true for those as well since the server delivers them.
 */
export function isServerMessage(msg: SignalingMessage): msg is ServerMessage {
  return (
    SERVER_ONLY_TYPES.has(msg.type) ||
    msg.type === "OFFER" ||
    msg.type === "ANSWER" ||
    msg.type === "ICE"
  );
}

// ---------------------------------------------------------------------------
// WebSocket Close Codes
// ---------------------------------------------------------------------------

/** Custom WebSocket close codes used by the signaling server. */
export enum WsCloseCode {
  /** Unsupported protocol version in connection URL. */
  INVALID_VERSION = 4000,
  /** Authentication failed during WebSocket upgrade. */
  AUTH_FAILED = 4001,
  /** User is banned. BANNED message sent before close. */
  BANNED = 4002,
  /** Too many connections from this IP. */
  RATE_LIMITED = 4003,
  /** No messages received for 90 seconds. */
  HEARTBEAT_TIMEOUT = 4008,
  /** Repeated invalid messages. */
  PROTOCOL_ERROR = 4009,
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Heartbeat interval in milliseconds (client sends every 30s). */
export const HEARTBEAT_INTERVAL_MS = 30_000;

/** Server closes connection after this many ms without any message. */
export const HEARTBEAT_TIMEOUT_MS = 90_000;

/** Maximum message size in bytes. */
export const MAX_MESSAGE_SIZE = 65_536; // 64 KB

/** Maximum SDP payload size in bytes. */
export const MAX_SDP_SIZE = 65_536; // 64 KB

/** Maximum ICE candidate size in bytes. */
export const MAX_CANDIDATE_SIZE = 2_048; // 2 KB

/** Stage 1 -> Stage 2 transition threshold in milliseconds. */
export const STAGE_2_THRESHOLD_MS = 30_000;

/** Stage 2 -> reveal eligibility threshold in milliseconds. */
export const REVEAL_ELIGIBLE_THRESHOLD_MS = 90_000;
